/**
 * cas-99b7: the ONLY place the app calls Firebase popup/redirect APIs.
 *
 * The app's Auth instance (auth-persistence.ts) has no popupRedirectResolver, on
 * purpose: one on the instance makes Firebase load gapi and the auth iframe on
 * every iPhone/Safari page load. Without a resolver, these SDK calls reject with
 * auth/argument-error, so each wrapper passes browserPopupRedirectResolver itself.
 *
 * Passing it per call costs nothing until the call runs. getRedirectResult only
 * starts the iframe flow when a redirect is actually pending, which it checks with
 * a sessionStorage read.
 *
 * popup-redirect-call-sites.test.ts refuses a direct call anywhere else.
 */
import {
  browserPopupRedirectResolver,
  getRedirectResult,
  reauthenticateWithPopup,
  signInWithPopup,
  signInWithRedirect,
  type Auth,
  type AuthProvider,
  type User,
  type UserCredential
} from 'firebase/auth';

export const signInWithProviderPopup = (
  auth: Auth,
  provider: AuthProvider
): Promise<UserCredential> => signInWithPopup(auth, provider, browserPopupRedirectResolver);

export const signInWithProviderRedirect = (auth: Auth, provider: AuthProvider): Promise<never> =>
  signInWithRedirect(auth, provider, browserPopupRedirectResolver);

export const reauthenticateWithProviderPopup = (
  user: User,
  provider: AuthProvider
): Promise<UserCredential> => reauthenticateWithPopup(user, provider, browserPopupRedirectResolver);

/** Runs on every auth init (stores/auth.ts). It resolves null unless a redirect sign-in is completing. */
export const readRedirectResult = (auth: Auth): Promise<UserCredential | null> =>
  getRedirectResult(auth, browserPopupRedirectResolver);
